import React, { useEffect, useMemo } from 'react';
import camelCase from 'lodash/camelCase';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { GetServerSideProps } from 'next';

import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Typography from 'src/components/ui/Typography';
import ActivityCard from 'src/components/community/ActivityCard';
import FullWidthLayout from 'src/components/other/FullWidthLayout';
import Image from 'src/components/ui/Image';

import { useActivityCategorySubcategoriesQuery } from 'src/hooks/__generated__/queries';

import { useManagedRouter } from 'src/hooks/useAppRouter';
import { AnalyticsEvent, EventParamKeys } from 'src/utils/analytics';
import BackLink from 'src/components/ui/BackLink';
import { colours } from 'src/constants/colours';
import type { ActivityCategoriesQuery, Activity, ActivityCategory } from 'src/types/__generated__/graphql';
import { ActivityCategories } from 'src/types/__generated__/graphql';
import { routePathCommunity, routePathCommunityCategory, routePathCommunitySearch } from 'src/constants/router';
import { styledTag } from 'src/utils/styled';
import { getAuthCookie } from 'src/utils/authCookie';
import getApolloClient from 'src/utils/getApolloClient';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';

const StyledImageContainer = styledTag('div')`
    position: relative;
    z-index: 5;
    height: 20rem;
    padding: 4rem 0;
    a {
        color: ${colours.white.main};
        &:hover {
            color: ${colours.white.main};
        }
    }
    .MuiContainer-root {
        height: 100%;
    }
`;

const BackgroundImageContainer = styledTag('div')`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: -1;
    pointer-events: none;
`;

const CenteredTextContainer = styledTag('div')`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    > * {
        pointer-events: all;
    }
`;

const CommunityCategoryPage: PageComponent = ({ category }: CommunityCategoryPageProps) => {
    const { router } = useManagedRouter();
    const { t } = useTranslation(['common', 'community']);

    useSeeCategoryLog(category);

    const activities: Activity[] = useMemo(() => {
        let sortedActivities: Activity[] = [];

        if (category?.activities) {
            sortedActivities = category.activities
                .slice()
                .sort((a, b) =>
                    t(`common:activities.${camelCase(a.labelKey)}`).localeCompare(
                        t(`common:activities.${camelCase(b.labelKey)}`),
                    ),
                ) as Activity[];
        }

        return sortedActivities;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category?.activities]);

    const { data: activityCategorySubcategories } = useActivityCategorySubcategoriesQuery({
        variables: {
            parentCategoryId: category?.id,
        },
    });

    return (
        <>
            <StyledImageContainer>
                <Container>
                    <BackLink href={routePathCommunity}>{t('common:back')}</BackLink>
                </Container>
                <CenteredTextContainer>
                    <Typography style={{ fontSize: '2.5rem' }} variant="h1" color="white" weight="medium" serif>
                        {t(`community:categories.${camelCase(category?.labelKey)}`)}
                    </Typography>
                </CenteredTextContainer>
                <BackgroundImageContainer>
                    <Image
                        src={`${
                            process.env.NEXT_PUBLIC_SITE_URL
                        }/assets/images/activity-category/${String(category?.labelKey)}.jpg`}
                        alt={String(category?.labelKey)}
                        style={{ objectFit: 'cover' }}
                        fill
                    />
                </BackgroundImageContainer>
            </StyledImageContainer>
            <Container>
                <Box py="2.5rem">
                    <Box mb="1.5rem">
                        <Typography variant="h2" serif>
                            {t('community:categoryActivities')}
                        </Typography>
                    </Box>
                    <Grid spacing={3} container>
                        {activities.map((activity) => (
                            <Grid
                                key={activity.id}
                                sm={3}
                                onClick={async () => {
                                    await router.push(
                                        `${routePathCommunitySearch}?activityId=${activity.id}&activityType=user`,
                                    );
                                }}
                                item
                            >
                                <ActivityCard activity={activity} greyBackground />
                            </Grid>
                        ))}
                        {activityCategorySubcategories?.activityCategorySubcategories?.map((subCategory) => (
                            <Grid
                                key={subCategory.id}
                                sm={3}
                                onClick={async () => {
                                    await router.push(`${routePathCommunityCategory}/${subCategory.id}`);
                                }}
                                item
                            >
                                <ActivityCard
                                    activity={subCategory as ActivityCategory}
                                    greyBackground
                                    activityCategory
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Container>
        </>
    );
};

const useSeeCategoryLog = (category: CommunityCategoryPageProps['category']) => {
    const { logEventDynamic } = useDynamicLogEvents();

    useEffect(() => {
        if (category) {
            logEventDynamic(AnalyticsEvent.SeeCategory, {
                [EventParamKeys.ItemId]: category.id,
                [EventParamKeys.ItemName]: category.labelKey,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category]);
};

CommunityCategoryPage.Layout = FullWidthLayout;

interface CommunityCategoryPageProps extends PageProps {
    category?: ArrayElement<ActivityCategoriesQuery['activityCategories']>;
}

export const getServerSideProps: GetServerSideProps<CommunityCategoryPageProps> = async (context) => {
    const auth = getAuthCookie(context);
    const apolloClient = getApolloClient(null, auth);
    const categoryId = context.params?.categoryId as string | undefined;
    let activityCategories;
    if (categoryId) {
        const { data } = await apolloClient.query<ActivityCategoriesQuery>({
            query: ActivityCategories,
        });
        activityCategories = data.activityCategories;
    }
    const category = activityCategories?.find((cat) => cat.id === categoryId);

    return {
        props: {
            category,
            ...(context.locale ? await serverSideTranslations(context.locale, ['common', 'community', 'profile']) : {}),
            SEOProps: {
                pageTitleTranslation: {
                    key: `common:seo.community.category.${camelCase(category?.labelKey)}`,
                },
            },
        },
    };
};

export default CommunityCategoryPage;
